import type { Object3D } from 'three';
import { isFeatureCurve } from '../mesh/feature';

/**
 * 线性零部件阵列方向几何解析器。
 * 圆形模型边按圆轴提供固定传播方向；其他参考委托注入端口，避免扩宽移动命令或到参考槽规则。
 * 本类不缓存场景对象或派生向量，装配姿态变化和历史恢复后总是从当前几何重新求解。
 */
export class LinearComponentPatternDirectionReferenceResolver implements PuppyCAD.LinearComponentPatternReferenceResolver {
  /** 既有直线、平面和基准几何解析端口。 */
  private readonly linearReferenceResolver: PuppyCAD.LinearComponentPatternReferenceResolver;

  /**
   * 注入既有参考解析能力。
   * @param linearReferenceResolver 不含圆形边扩展的线性参考端口。
   */
  public constructor(linearReferenceResolver: PuppyCAD.LinearComponentPatternReferenceResolver) {
    this.linearReferenceResolver = linearReferenceResolver;
  }

  /**
   * 只检查方向候选类型，供高频悬停使用。
   * @param object 待选择的几何对象。
   * @returns 圆形边或既有有效类型返回 true，不在此阶段采样或构造世界向量。
   */
  public isSupportedReferenceObject(object: Object3D | null | undefined): boolean {
    if (isFeatureCurve(object)) return object.curveKind === 'circle';
    return this.linearReferenceResolver.isSupportedReferenceObject(object);
  }

  /**
   * 校验引用的实际几何，在写入命令状态和恢复定义前拒绝退化圆边。
   * @param object 待校验引用。
   * @returns 是否可以提供有效方向。
   */
  public canUseAsReference(object: Object3D | null | undefined): boolean {
    return this.resolve(object ?? null) !== null;
  }

  /**
   * 求解当前世界空间的阵列方向。
   * @param object 方向参考对象。
   * @returns 圆边以圆心为锚点、圆轴为方向；失败返回 null，禁止回退到曲线首段弦。
   */
  public resolve(object: Object3D | null): PuppyCAD.LinearComponentPatternResolvedReference | null {
    if (!isFeatureCurve(object)) return this.linearReferenceResolver.resolve(object);
    const circle = object.getWorldCircleGeometry();
    if (!circle) return null;
    return { anchorWorld: circle.center, directionWorld: circle.normal, planeNormalWorld: null };
  }
}
